import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { ESRI } from '../../../esri-loader-util';
import { BooleanConverter, InputConverter } from '../../../inputConverter';
import { LayerService } from '../../../services/layer.service';
import { SpatialQueryService } from '../../../services/spatialQuery.service';
import { ThemeService } from '../../../services/theme.service';
import { ToSqlStringService } from '../../../services/to-sql-string.service';
import { BaseComponent } from '../../base.component';
import { CreationMode, ISketchTool } from '../../mapComponents/sketch-tool/sketch-tool.component';

let Graphic: __esri.GraphicConstructor;

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-lasso-tool-ng',
    styleUrls: ['../../sharedStyles/base.scss', './lasso-tool.component.scss'],
    templateUrl: './lasso-tool.component.html'
})

export class LassoToolComponent extends BaseComponent implements OnInit {

    @ViewChild('sketchTool') private readonly sketchToolRef: ISketchTool;

    r = 255; g = 165; b = 0; // Orange color
    @Input() containerElement: any;
    @Input() featureLayerColor: number[] = [this.r, this.g, this.b];
    @Input() guid: string;
    @Input() sketchToolType: string = 'polygon';
    @Input() sketchCreationMode: CreationMode = 'single';
    @Input() sketchDefaultCreateOptions: NonNullable<__esri.SketchDefaultCreateOptions> = { mode: 'hybrid' };
    @Input() sketchDefaultUpdateOptions: NonNullable<__esri.SketchDefaultUpdateOptions> = { tool: 'transform' };
    @Input() spatialRelationships: string[] = ['Intersects', 'Contains'];
    @Input() featuresLayer: NonNullable<__esri.GraphicsLayer>;
    @Input() geoprocessingLayers: NonNullable<__esri.Layer>[] = [];
    @Input() spatialQueryType: string = 'feature';
    @Input() spatialRelationship: string = this.spatialRelationships[0];
    @Input() esriLayers: NonNullable<__esri.Layer[]>;
    @Input() mapView: NonNullable<__esri.MapView>;
    @Input() layerFeatureResults: ILayerFeatureResult[];
    @Input() @InputConverter(BooleanConverter) runSpatialQueryOnSketchComplete: boolean = true;
    @Input() @InputConverter(BooleanConverter) reactivateSketchOnSketchComplete: boolean = true;
    @Input() @InputConverter(BooleanConverter) displayFeatureGraphicsAfterSpatialQuery: boolean = true;

    @Output() readonly oncomplete = new EventEmitter<boolean>();
    @Output() readonly drawEnd = new EventEmitter<__esri.Geometry[]>();
    constructor(_vcr: ViewContainerRef,
        private readonly layerService: LayerService,
        private readonly spatialQueryService: SpatialQueryService,
        private readonly toSqlStringService: ToSqlStringService,
        private readonly themeService: ThemeService) {
        super(_vcr);
        this.registerEvent('oncomplete', this.oncomplete);
        this.registerEvent('drawEnd', this.drawEnd);
    }

    async ngOnInit() {
        [Graphic] = await Promise.all([ESRI.Graphic]);
        this.sketchTool.oncomplete.subscribe(() => {
            this.sketchComplete();
            this.drawEnd.emit(this.sketchTool.geometries);
        });
    }

    _isActivated: boolean = false;
    @Input()
    get isActivated(): boolean {
        return this._isActivated;
    }

    @Input()
    get sketchTool(): ISketchTool {
        return this.sketchToolRef;
    }

    @Input()
    public activate = async () => {
        await this.addFeaturesLayerToMapIfNotPresent();
        this.sketchTool.mapView = this.mapView;
        this.sketchTool.creationMode = this.sketchCreationMode;
        this.sketchTool.defaultCreateOptions = this.sketchDefaultCreateOptions;
        this.sketchTool.defaultUpdateOptions = this.sketchDefaultUpdateOptions;
        if (this.containerElement) {
            this.sketchTool.containerElement = this.containerElement;
        } else {
            this.sketchTool.containerElement = this.hostElement;
        }
        this.sketchTool.activate(this.sketchToolType);
        this._isActivated = true;
    }

    @Input()
    public deactivate = async () => {
        this._isActivated = false;
        this.sketchTool.deactivate();
    }

    @Input()
    public destroy = async () => {
        this.deactivate();
        this.clearFeatureResults();
        if (this.featuresLayer) {
            this.featuresLayer.removeAll();
            this.mapView.map.remove(this.featuresLayer);
        }
        if (this.geoprocessingLayers.length) {
            for (const layer of this.geoprocessingLayers) {
                this.mapView.map.remove(layer);
            }
            this.geoprocessingLayers = [];
        }
        this.sketchTool.destroy();
    }

    @Input()
    public clearFeatureResults = () => {
        if (this.featuresLayer) {
            this.featuresLayer.removeAll();
        }
        this.layerFeatureResults = null;
        this.removeGeoProcessingLayers();
    }

    @Input()
    public runSpatialQuery = async () => {
        if (!this.layerFeatureResults) {
            this.layerFeatureResults = [];
        }
        try {
            if (this.spatialQueryType === 'feature') {
                await this.runFeatureQuery();
            } else {
                await this.runGeoprocessingQuery();
            }
            this.oncomplete.emit(true);
        }
        catch (ex) {
            this.oncomplete.emit(false);
        }
        if (!this.reactivateSketchOnSketchComplete) {
            this.sketchTool.deactivate();
        }
    }

    async runFeatureQuery() {
        const sublayers = [];
        for (const layer of this.esriLayers) {
            if ((<any>layer).sublayers) {
                for (const sublayer of (<any>layer).sublayers.items) {
                    sublayers.push(sublayer);
                }
            }
        }
        for (const sublayer of sublayers) {
            const clonedSublayer = sublayer.clone();
            const query = clonedSublayer.createQuery();
            query.geometry = this.sketchTool.geometries[0];
            query.outFields = ['*'];
            query.spatialRelationship = this.spatialRelationship.toLowerCase();
            query.returnGeometry = true;
            const result = await clonedSublayer.queryFeatures(query);
            const layerFeatureResult: ILayerFeatureResult = { sublayer, featureResults: result.features };
            if (!result.features) { continue; }
            this.layerFeatureResults.push(layerFeatureResult);
        }
        if (this.displayFeatureGraphicsAfterSpatialQuery) {
            this.displayFeaturesOnMap();
        }
    }

    async runGeoprocessingQuery() {
        this.removeGeoProcessingLayers();
        if (this.sketchTool.geometries && this.sketchTool.geometries.length) {
            const joinedGeometries = { rings: (<any>this.sketchTool.geometries[0]).rings };
            const joinGeometriesString = JSON.stringify(joinedGeometries);
            const dynamicLayerSpatialLayers = this.esriLayers.map(x => this.spatialQueryService.getIDynamicLayerSpatialFromLayer(x));
            const results: [{ id: string; guid: string }] = await this.spatialQueryService.spatialQuery(true, dynamicLayerSpatialLayers, null, null, null, [joinGeometriesString]);
            for (const result of results) {
                this.guid = result.guid;
                const esriLayer = this.layerService.getEsriLayerByUniqueId(result.id);
                const mapcoreProps = esriLayer.mapcoreProps;
                const query = this.toSqlStringService.getSimplifiedDeepCopyOfQuery(mapcoreProps.query);
                const queryWithGuidCondition = this.toSqlStringService.addFilter(query, this.spatialQueryService.getGuidQuery(result.guid));
                const layerSettings: any = { id: mapcoreProps.parentId, query: queryWithGuidCondition, hideInLegend: true };
                this.geoprocessingLayers = await this.layerService.addMapcoreLayersToMap([layerSettings]);
            }
        }
    }

    async displayFeaturesOnMap() {
        for (const layerFeatureResult of this.layerFeatureResults) {
            const geometryType = (<any>layerFeatureResult.sublayer).source.dataSource.geometryType;
            const symbolDefiniton = this.themeService.getSymbolDefinitionFromGeometry(geometryType, this.featureLayerColor);
            for (const feature of layerFeatureResult.featureResults) {
                const graphic = new Graphic({
                    geometry: feature.geometry,
                    symbol: symbolDefiniton
                });
                this.featuresLayer.add(graphic);
            }
        }
    }

    async removeGeoProcessingLayers() {
        this.guid = null;
        if (this.mapView && this.geoprocessingLayers) {
            this.mapView.map.removeMany(this.geoprocessingLayers);
        }
    }

    async sketchComplete() {
        if (this._isActivated && this.runSpatialQueryOnSketchComplete) {
            await this.runSpatialQuery();
        }
        if (this.reactivateSketchOnSketchComplete) {
            this.sketchTool.graphicsLayer.removeAll();
            this.sketchTool.activate(this.sketchToolType);
        }
    }

    async addFeaturesLayerToMapIfNotPresent() {
        if (this.mapView) {
            if (!this.featuresLayer) {
                this.featuresLayer = await this.layerService.addGraphicsLayer({ title: 'Lasso Tool', hideInLegend: true });
                this.mapView.map.allLayers.on('before-add', (event) => {
                    if (this.isActivated && event.item.listMode === 'hide') {
                        event.item.set('hideInLegend', true);
                    }
                });
            }
            if (!this.mapView.map.allLayers.some(x => x === this.featuresLayer)) {
                this.mapView.map.add(this.featuresLayer);
            }
            this.layerService.moveLayerToTheTop(this.featuresLayer);
            this.featuresLayer.visible = true;
        }
    }
}

export interface ILayerFeatureResult {
    sublayer: __esri.Sublayer;
    featureResults: __esri.Graphic[];
}
